import {
  type Exact,
  exactPi,
  exactRational,
} from "@/lib/tool-kit/geometry/exact";
import { BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import {
  absR,
  divR,
  mulR as mulRational,
  negR,
  parseRational,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * An angle typed by a reader, in degrees or in radians.
 *
 * Radians are usually written as multiples of π — `π/3`, `2pi/3`, `0.25π` — and those
 * stay exact: π/3 is 60° with nothing rounded. A plain radian number such as `1.2` is
 * exact as typed, but its degrees (68.7549…°) are not, so they are left as a float.
 */
export interface ParsedAngle {
  /** Exact degrees: any degree input, or a radian input that is a multiple of π. */
  degrees: Rational | null;
  /** Exact radians: (degrees/180)·π, c·π, or a plain rational. */
  radians: Exact;
  degreesValue: number;
  radiansValue: number;
  /** How the radian measure is written back: "π/3", "1.2". */
  radianText: string;
}

export type AngleInputUnit = "deg" | "rad";

const DEGREE_SUFFIX = /\s*(°|º|deg(?:rees?)?)\s*$/i;
const PI_MULTIPLE =
  /^([+\-−]?)\s*([\d.,]*)\s*\*?\s*(?:π|pi)\s*(?:\/\s*([\d.,]+))?$/i;
const RADIAN_SUFFIX = /\s*(rad(?:ians?)?)\s*$/i;

const ONE: Rational = parseRational("1") as Rational;
const HUNDRED_EIGHTY: Rational = parseRational("180") as Rational;

function fromDegrees(degrees: Rational): ParsedAngle {
  const piCoefficient = divR(degrees, HUNDRED_EIGHTY);
  return {
    degrees,
    radians: exactPi(piCoefficient),
    degreesValue: toNumberR(degrees),
    radiansValue: toNumberR(degrees) * (Math.PI / 180),
    radianText: piMultipleText(piCoefficient),
  };
}

/** c·π written the short way: "π", "π/3", "2π/3", "−π/2", "0.5π". */
export function piMultipleText(coefficient: Rational): string {
  if (coefficient.num === BIG_ZERO) {
    return "0";
  }
  const sign = signR(coefficient) < 0 ? "−" : "";
  const top = absR(coefficient).num;
  const lead = top === BIG_ONE ? "" : top.toString();
  const tail =
    coefficient.den === BIG_ONE ? "" : `/${coefficient.den.toString()}`;
  return `${sign}${lead}π${tail}`;
}

function fromPiMultiple(match: RegExpExecArray): ParsedAngle | null {
  const negative = match[1] === "-" || match[1] === "−";
  const lead = match[2] === "" ? ONE : parseRational(match[2]);
  const divisor = match[3] === undefined ? ONE : parseRational(match[3]);
  if (lead === null || divisor === null || signR(divisor) === 0) {
    return null;
  }
  const magnitude = divR(lead, divisor);
  const coefficient = negative ? negR(magnitude) : magnitude;
  return fromDegrees(mulRational(coefficient, HUNDRED_EIGHTY));
}

/** Read `text` as an angle in `unit`, or null when it is not a number. */
export function parseAngleInput(
  text: string,
  unit: AngleInputUnit
): ParsedAngle | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  if (unit === "deg") {
    const value = parseRational(trimmed.replace(DEGREE_SUFFIX, ""));
    return value === null ? null : fromDegrees(value);
  }
  const body = trimmed.replace(RADIAN_SUFFIX, "");
  const match = PI_MULTIPLE.exec(body);
  if (match) {
    return fromPiMultiple(match);
  }
  const value = parseRational(body);
  if (value === null) {
    return null;
  }
  return {
    degrees: null,
    radians: exactRational(value),
    degreesValue: toNumberR(value) * (180 / Math.PI),
    radiansValue: toNumberR(value),
    radianText: body,
  };
}

/** A number that may be a multiple of π: `10π`, `pi/3`, `2.5`. */
export interface PiNumber {
  coefficient: Rational;
  hasPi: boolean;
}

/** Read `text` as a plain number or a rational multiple of π, or null. */
export function parsePiNumber(text: string): PiNumber | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  const match = PI_MULTIPLE.exec(trimmed);
  if (match) {
    const negative = match[1] === "-" || match[1] === "−";
    const lead = match[2] === "" ? ONE : parseRational(match[2]);
    const divisor = match[3] === undefined ? ONE : parseRational(match[3]);
    if (lead === null || divisor === null || signR(divisor) === 0) {
      return null;
    }
    const magnitude = divR(lead, divisor);
    return { coefficient: negative ? negR(magnitude) : magnitude, hasPi: true };
  }
  const value = parseRational(trimmed);
  return value === null ? null : { coefficient: value, hasPi: false };
}
